import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../globals";
import HttpManager from "../../../services/HttpManager";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { ProspectCatalogs, ProspectRow } from "../../../services/v2/prospects.service";
import ClienteView from "./clienteView";
import MarcadorView from "./marcadorView";
import NumerosModal from "./numerosModal";
import TableroView from "./tableroView";
import CarteraView, { CarteraSelect } from "./carteraView";
import CampanasView from "./campanasView";
import { TelefonoBoton, TelefonoPanel } from "./telefono/telefonoUI";
import NewProspectModal from "./newProspectModal";
import {
	cuando, errorTexto, ETAPAS, nombreCompleto, OBJETIVOS_BARRA, resumenWapp, statusClase, TITULO_VISTA, ultimoMensaje, vencido, vistasDeEtapa
} from "./salesHelpers";
import "./sales.css";

const PER_PAGE = 100;

/**
 * Ventas: la bandeja que reemplaza al CRM de Zoho. Tres paneles — vistas con conteo, lista, cliente —
 * y la barra de Etapa arriba, que separa a quien pide información (Pre-Inscripción) de a quien ya es alumno.
 * Supervisión puede ver la cartera de todo el plantel o la de nadie ("sin asesora").
 */
const SalesController: React.FC = () => {
	const http = HttpManager.getInstance();
	// El plantel de la BARRA, igual que Matrículas (2026-09-24); el de la sesión es respaldo.
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;

	const [catalogs, setCatalogs] = useState<ProspectCatalogs | null>(null);
	const [etapa, setEtapa] = useState<number>(1);
	const [owner, setOwner] = useState<string>("me");
	const [view, setView] = useState<string | null>(null);
	const [search, setSearch] = useState<string>("");
	const [q, setQ] = useState<string>("");
	const [counts, setCounts] = useState<{ [k: string]: number }>({});
	const [etapaCounts, setEtapaCounts] = useState<{ [k: string]: number }>({});
	const [rows, setRows] = useState<ProspectRow[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [page, setPage] = useState<number>(1);
	const [sel, setSel] = useState<number | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [nuevo, setNuevo] = useState<boolean>(false);
	const [marcador, setMarcador] = useState<boolean>(false);
	const [numeros, setNumeros] = useState<boolean>(false);
	const [tablero, setTablero] = useState<boolean>(false);
	const [cartera, setCartera] = useState<boolean>(false);
	const [campanas, setCampanas] = useState<boolean>(false);
	// Barra de arriba (lienzo): Objetivo y Día · Mañana · Tarde.
	const [objetivo, setObjetivo] = useState<string>("");
	// Cartera (supervisión): marcar varias personas y reasignarlas de una vez, o bajar la lista a Excel.
	const [marcados, setMarcados] = useState<number[]>([]);
	const [reasignarA, setReasignarA] = useState<string>("");
	const [busyBloque, setBusyBloque] = useState<boolean>(false);

	const reasignarBloque = async () => {
		if (!campusId || !reasignarA || !marcados.length) return;
		setBusyBloque(true); setError(null);
		try {
			const r = await http.prospectsService.assignBulk(campusId, marcados, reasignarA, null);
			setMarcados([]); setReasignarA("");
			if (r.data.fallas.length) setError(`${r.data.reasignados} reasignados · ${r.data.fallas.length} no se pudieron`);
			refrescar();
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusyBloque(false); }
	};
	const bajarExcel = async () => {
		if (!campusId) return;
		try {
			const r = await http.prospectsService.export(campusId, { ...filtros, view: q ? null : view });
			const url = URL.createObjectURL(r.data);
			const a = document.createElement("a"); a.href = url; a.download = "cartera.csv"; a.click();
			setTimeout(() => URL.revokeObjectURL(url), 2000);
		} catch (e) { setError(errorTexto(e)); }
	};

	useEffect(() => {
		if (!campusId) return;
		http.prospectsService.catalogs(campusId).then((r) => { setCatalogs(r.data); if (r.data.isSupervisor) setOwner((o) => (o === "me" ? "all" : o)); }).catch((e) => setError(errorTexto(e)));
	}, [campusId]);

	// Buscar cruza Etapas: quien llama puede ser alumno, no solo prospecto.
	const filtros = q ? { owner, q } : { etapa, owner, objetivo: objetivo || null };

	// Solo pinta la respuesta más reciente: al abrir, "Mi cartera" y "Todo el plantel" salen casi juntas y la lenta no debe pisar a la buena.
	const turnoConteos = useRef<number>(0);
	const turnoLista = useRef<number>(0);
	const cargarConteos = useCallback(async () => {
		if (!campusId) return;
		const yo = ++turnoConteos.current;
		try {
			// el riel cuenta la cartera (con el objetivo), no la búsqueda
			const r = await http.prospectsService.views(campusId, { etapa, owner, objetivo: objetivo || null });
			if (yo !== turnoConteos.current) return;
			setCounts(r.data.counts || {});
			setEtapaCounts(r.data.etapas || {});
		} catch (e) { setError(errorTexto(e)); }
	}, [campusId, etapa, owner, objetivo]);

	const cargarLista = useCallback(async (p: number) => {
		if (!campusId) return;
		const yo = ++turnoLista.current;
		setLoading(true); setError(null);
		try {
			const r = await http.prospectsService.list(campusId, { ...filtros, view: q ? null : view }, PER_PAGE, p);
			if (yo !== turnoLista.current) return;
			setRows(r.data.data); setTotal(r.data.total); setPage(p);
		} catch (e) { if (yo === turnoLista.current) setError(errorTexto(e)); }
		finally { if (yo === turnoLista.current) setLoading(false); }
	}, [campusId, etapa, owner, q, view, objetivo]);

	useEffect(() => { cargarConteos(); }, [cargarConteos]);
	useEffect(() => { cargarLista(1); }, [cargarLista]);

	const refrescar = () => { cargarConteos(); cargarLista(page); };
	const irAVista = (e: number, v: string) => { setCartera(false); setCampanas(false); setTablero(false); setMarcador(false); setEtapa(e); setView(v); setObjetivo(""); setSearch(""); setQ(""); setSel(null); };
	const objetivosEtapa = OBJETIVOS_BARRA.filter((o) => o.etapas.includes(etapa));

	if (!campusId) {
		return <div className="ventas"><div className="ventas-vacio">Elige un plantel en la barra de arriba.</div></div>;
	}

	const paginas = Math.max(1, Math.ceil(total / PER_PAGE));
	const etiquetaCartera = owner === "me" ? "Mi cartera" : owner === "none" ? "Sin asesora" : owner === "all" ? "Todo el plantel"
		: owner.startsWith("team:") ? "Equipo " + owner.slice(5) : owner.startsWith("z:") ? (catalogs?.advisors.find((a) => "z:" + a.id === owner)?.name || "Asesora") : "Cartera";

	return (
		<div className={"ventas" + (sel ? " con-cliente" : "")}>
			<div className="ventas-etapas">
				{ETAPAS.map((e) => (
					<button key={e.id} className={"ventas-etapa" + (etapa === e.id ? " activa" : "")} onClick={() => { setEtapa(e.id); setView(null); setObjetivo(""); setSearch(""); setQ(""); setSel(null); setCartera(false); }}>
						<span>{e.name}</span>
						<span className="n">{(etapaCounts[e.id] || 0).toLocaleString("es-MX")}</span>
					</button>
				))}
				{!tablero && !cartera && !campanas ? (
					<div className="ventas-objetivo">
						<label>Objetivo
							<select value={objetivo} onChange={(e) => { setObjetivo(e.target.value); setSel(null); }} aria-label="Objetivo">
								<option value="">Todos</option>
								{objetivosEtapa.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
							</select>
						</label>
					</div>
				) : null}
				<div className="ventas-herr">
					<TelefonoBoton campusId={campusId} />
					<button className={"btn-wa" + (marcador ? " pri" : " llamar")} onClick={() => { setMarcador(!marcador); setCartera(false); setCampanas(false); setTablero(false); setSel(null); }}>{marcador ? "Salir del auto-marcador" : "Auto-marcador"}</button>
					<button className={"btn-wa" + (cartera ? " pri" : "")} onClick={() => { setCartera(!cartera); setMarcador(false); setCampanas(false); setTablero(false); setSel(null); }}>Cartera</button>
					{catalogs?.isSupervisor ? <button className={"btn-wa" + (campanas ? " pri" : "")} onClick={() => { setCampanas(!campanas); setMarcador(false); setCartera(false); setTablero(false); setSel(null); }}>Campañas</button> : null}
					{catalogs?.isSupervisor ? <button className={"btn-wa" + (tablero ? " pri" : "")} onClick={() => { setTablero(!tablero); setMarcador(false); setCartera(false); setCampanas(false); setSel(null); }}>Tablero</button> : null}
					{catalogs?.isSupervisor ? <button className="btn-wa" onClick={() => setNumeros(true)}>Números</button> : null}
					{catalogs?.isSupervisor && !cartera ? <CarteraSelect catalogs={catalogs} value={owner} onChange={(v) => { setOwner(v); setSel(null); }} /> : null}
				</div>
			</div>

			{tablero ? <TableroView campusId={campusId} onSalir={() => setTablero(false)} />
			: campanas ? <CampanasView campusId={campusId} catalogs={catalogs} onSalir={() => setCampanas(false)} />
			: cartera ? <CarteraView campusId={campusId} catalogs={catalogs} owner={owner} onOwner={(v) => setOwner(v)} onSalir={() => setCartera(false)}
				onOpen={(id) => { setCartera(false); setSel(id); }} />
			: marcador ? <MarcadorView campusId={campusId} catalogs={catalogs} etapa={etapa} owner={owner} onSalir={() => setMarcador(false)} /> : (
			<div className="ventas-cuerpo">
				<div className="ventas-riel">
					<button className="ventas-nuevo" onClick={() => setNuevo(true)}>+ Nuevo prospecto</button>
					<button className={"vista" + (!q && view === null ? " activa" : "")} onClick={() => { setView(null); setSearch(""); setQ(""); setSel(null); }}>
						<span style={{ fontWeight: 600 }}>{etiquetaCartera}</span>
						<span className="cnt">{(counts.total || 0).toLocaleString("es-MX")}</span>
					</button>
					{vistasDeEtapa(etapa).map((g) => (
						<React.Fragment key={g.grupo}>
							<div className="grupo">{g.grupo}</div>
							{g.items.map((i) => (
								<button key={i.key} className={"vista" + (!q && view === i.key ? " activa" : "")} onClick={() => { setView(i.key); setSearch(""); setQ(""); setSel(null); }}>
									<span>{i.label}</span>
									<span className={"cnt" + ((counts[i.key] || 0) > 0 && i.alerta ? " alerta" : (counts[i.key] || 0) > 0 && i.fuerte ? " fuerte" : "")}>
										{(counts[i.key] || 0).toLocaleString("es-MX")}
									</span>
								</button>
							))}
						</React.Fragment>
					))}
				</div>

				<div className="ventas-lista">
					{/* Pantalla angosta: el riel se esconde y las vistas pasan a este selector. */}
					<select className="vista-movil" value={view || ""} aria-label="Vista"
						onChange={(e) => { setView(e.target.value || null); setSearch(""); setQ(""); setSel(null); }}>
						<option value="">{etiquetaCartera + " · " + (counts.total || 0).toLocaleString("es-MX")}</option>
						{vistasDeEtapa(etapa).map((g) => (
							<optgroup key={g.grupo} label={g.grupo}>
								{g.items.map((i) => <option key={i.key} value={i.key}>{i.label + " · " + (counts[i.key] || 0).toLocaleString("es-MX")}</option>)}
							</optgroup>
						))}
					</select>
					<div className="busca">
						<input type="search" placeholder="Teléfono, nombre o apellido" value={search} aria-label="Buscar"
							onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { setQ(search.trim()); setSel(null); } if (e.key === "Escape") { setSearch(""); setQ(""); } }} />
							{q ? <button className="limpiar" onClick={() => { setSearch(""); setQ(""); }} aria-label="Quitar búsqueda">×</button> : null}
					</div>
					{catalogs?.isSupervisor ? (
						<div className="ventas-bloque">
							{marcados.length ? (
								<>
									<span>{marcados.length} marcados</span>
									<select value={reasignarA} onChange={(e) => setReasignarA(e.target.value)} aria-label="Reasignar a">
										<option value="">Reasignar a…</option>
										{(catalogs.advisors || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
									</select>
									<button className="btn-wa pri" disabled={!reasignarA || busyBloque} onClick={reasignarBloque}>Reasignar</button>
									<button className="btn-wa" onClick={() => setMarcados([])}>Quitar</button>
								</>
							) : (
								<>
									<button className="btn-wa" onClick={() => setMarcados(rows.map((r) => r.id))}>Marcar todos</button>
									<button className="btn-wa" onClick={bajarExcel}>Excel</button>
								</>
							)}
						</div>
					) : null}
					<div className="titulo"><b>{q ? "Búsqueda: " + q : TITULO_VISTA(view)}</b><span style={{ fontSize: 12, color: "var(--wa-apagado)" }}>{loading ? "…" : total.toLocaleString("es-MX")}</span></div>
					{error ? <div className="aviso error" style={{ margin: 10 }}>{error}</div> : null}
					<div className="filas" style={loading ? { opacity: .45 } : undefined}>
						{rows.map((r) => (
							<button key={r.id} className={"ventas-fila" + (sel === r.id ? " activa" : "") + (marcados.includes(r.id) ? " marcada" : "")} onClick={() => setSel(r.id)}>
								<div className="l1">
									{catalogs?.isSupervisor ? (
										<input type="checkbox" className="marca" checked={marcados.includes(r.id)} aria-label={"Marcar a " + nombreCompleto(r)}
											onClick={(e) => e.stopPropagation()}
											onChange={(e) => setMarcados(e.target.checked ? [...marcados, r.id] : marcados.filter((x) => x !== r.id))} />
									) : null}
									<span className="nombre">{nombreCompleto(r)}</span>
									{r.next_action_at ? <span className={"cuando" + (vencido(r.next_action_at) ? " vencida" : "")}>{cuando(r.next_action_at)}</span>
										: <span className="cuando">{cuando(r.last_inbound_at || r.last_activity_at)}</span>}
								</div>
								<div className="l2">{ultimoMensaje(r.last_message) || [r.phone_e164 ? telefonoBonito(r.phone_e164) : null, r.course_interest_text].filter(Boolean).join(" · ")}</div>
								{r.wapp ? <div className="l3">{resumenWapp(r.wapp)}</div> : null}
								<div className="chips">
									{q ? <span className="chip">{ETAPAS.find((e) => e.id === r.etapa_id)?.name}</span> : null}
									<span className={"chip " + statusClase(r.status_id)}>{r.status}</span>
									{r.source ? <span className="chip">{r.source}</span> : null}
									{owner !== "me" && r.owner_zoho_name ? <span className="chip">{r.owner_zoho_name.split(" ")[0]}</span> : null}
									{(r.gestiones || []).map((g) => <span key={g.id} className="chip objetivo" title={g.owner ? "a cargo de " + g.owner : undefined}>{g.code === "promo" ? "Promo" : g.name}{g.owner ? " · " + g.owner.split(" ")[0] : ""}</span>)}
								</div>
							</button>
						))}
						{!loading && !rows.length ? <div className="ventas-vacio">Nada en esta vista.</div> : null}
					</div>
					<div className="ventas-pag">
						<button disabled={page <= 1} onClick={() => cargarLista(page - 1)}>Anterior</button>
						<span>{page} de {paginas}</span>
						<button disabled={page >= paginas} onClick={() => cargarLista(page + 1)}>Siguiente</button>
					</div>
				</div>

				{sel ? <ClienteView campusId={campusId} prospectId={sel} catalogs={catalogs} onChanged={refrescar} onOpen={setSel} onBack={() => setSel(null)} onVista={irAVista} />
					: <div className="ventas-cliente"><div className="ventas-vacio">Elige una persona de la lista.</div></div>}
			</div>
			)}

			<TelefonoPanel campusId={campusId} onAbrir={(id) => { if (!marcador) { setCartera(false); setCampanas(false); setTablero(false); setSel(id); } }} />
			{numeros ? <NumerosModal campusId={campusId} catalogs={catalogs} onClose={() => setNumeros(false)} /> : null}
			{nuevo ? (
				<NewProspectModal campusId={campusId} catalogs={catalogs} onClose={() => setNuevo(false)}
					onCreated={(id) => { setNuevo(false); setSel(id); refrescar(); }}
					onOpenExisting={(id) => { setNuevo(false); setSel(id); }} />
			) : null}
		</div>
	);
};

export default SalesController;
